/**
 * VariableHelper - Dynamic variable panel for Template Builder
 *
 * Displays all available template variables organized by category:
 * - Fixed variables (empresa, questionário, resultados, componentes)
 * - Dynamic grupo variables (loaded from questionário)
 *
 * Grupo variables use order-based syntax: {{grupo[ordem].propriedade}}
 * where ordem starts at 1 (1-indexed based on ordemQuestionario)
 *
 * Phase 3 of Template Builder
 */

import {
  Stack,
  Accordion,
  TextInput,
  Card,
  Text,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
  Alert,
  Loader,
  Code,
  Select,
  Box
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconCopy,
  IconCheck,
  IconAlertCircle,
  IconBuilding,
  IconFileText,
  IconChartBar,
  IconComponents,
  IconDimensions,
  IconBook
} from "@tabler/icons-react";
import { useState, useMemo, useCallback, memo } from "react";
import {
  TEMPLATE_VARIABLES,
  GROUP_VARIABLE_PROPERTIES,
  VariableCategory,
  type TemplateVariable,
  type GroupVariableProperty
} from "../../constants/template-variables";
import { useQuestionarioGroups, type GrupoForVariable } from "../../hooks/useQuestionarioGroups";

/** Props for VariableCard component */
interface VariableCardProperties {
  variable: TemplateVariable;
  compact: boolean;
  copiedVariable: string | null;
  onCopy: (variableName: string, displayName: string) => void;
}

/**
 * Card component for displaying a single template variable
 * Extracted to avoid inline functions in parent
 */
const VariableCard = memo(function VariableCard({ variable, compact, copiedVariable, onCopy }: VariableCardProperties) {
  const handleClick = useCallback(() => {
    onCopy(variable.name, variable.label);
  }, [onCopy, variable.name, variable.label]);

  const handleCopyClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onCopy(variable.name, variable.label);
    },
    [onCopy, variable.name, variable.label]
  );

  return (
    <Card padding={compact ? "xs" : "sm"} radius="sm" withBorder style={{ cursor: "pointer" }} onClick={handleClick}>
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={compact ? 2 : 4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text size={compact ? "sm" : "md"} fw={500}>
              {variable.label}
            </Text>
            {variable.type === "component" && (
              <Badge size="xs" variant="light" color="blue">
                componente
              </Badge>
            )}
          </Group>

          <Text size="xs" c="dimmed">
            {variable.description}
          </Text>

          <Code block={false} style={{ fontSize: "11px" }}>
            {`{{${variable.name}}}`}
          </Code>

          {!compact && (
            <Text size="xs" c="dimmed" fs="italic">
              Exemplo: {variable.example}
            </Text>
          )}
        </Stack>

        <Tooltip label="Copiar variável">
          <ActionIcon variant="light" color="blue" onClick={handleCopyClick}>
            {copiedVariable === variable.name ? <IconCheck size={18} /> : <IconCopy size={18} />}
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
});

/** Props for GrupoVariableCard component */
interface GrupoVariableCardProperties {
  grupo: GrupoForVariable;
  property: GroupVariableProperty;
  compact: boolean;
  copiedVariable: string | null;
  onCopy: (grupo: GrupoForVariable, property: GroupVariableProperty) => void;
}

/**
 * Card component for displaying a grupo variable property
 * Extracted to avoid inline functions in parent
 */
const GrupoVariableCard = memo(function GrupoVariableCard({
  grupo,
  property,
  compact,
  copiedVariable,
  onCopy
}: GrupoVariableCardProperties) {
  const variableName = `grupo[${grupo.ordem}].${property.property}`;
  const uniqueKey = `grupo[${grupo.ordem}].${property.property}`;

  const handleClick = useCallback(() => {
    onCopy(grupo, property);
  }, [onCopy, grupo, property]);

  const handleCopyClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onCopy(grupo, property);
    },
    [onCopy, grupo, property]
  );

  return (
    <Card padding={compact ? "xs" : "sm"} radius="sm" withBorder style={{ cursor: "pointer" }} onClick={handleClick}>
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={compact ? 2 : 4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text size={compact ? "sm" : "md"} fw={500}>
              {property.label}
            </Text>
            {property.type === "component" && (
              <Badge size="xs" variant="light" color="blue">
                componente
              </Badge>
            )}
          </Group>

          <Text size="xs" c="dimmed">
            {property.description}
          </Text>

          <Code block={false} style={{ fontSize: "11px" }}>
            {`{{${variableName}}}`}
          </Code>

          {!compact && (
            <Text size="xs" c="dimmed" fs="italic">
              Exemplo: {property.example}
            </Text>
          )}
        </Stack>

        <Tooltip label="Copiar variável">
          <ActionIcon variant="light" color="blue" onClick={handleCopyClick}>
            {copiedVariable === uniqueKey ? <IconCheck size={18} /> : <IconCopy size={18} />}
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
});

/** Questionário option for select */
interface QuestionarioOption {
  id: string;
  nome: string;
}

export interface VariableHelperProperties {
  /** Questionário ID to load grupos from */
  questionarioId?: string;
  /** Callback when questionário selection changes */
  onQuestionarioChange?: (questionarioId: string | null) => void;
  /** Callback when a variable is selected/copied */
  onVariableSelect?: (variable: string) => void;
  /** Enable compact mode (smaller cards) */
  compact?: boolean;
  /** Questionários disponíveis para seleção */
  questionarios?: QuestionarioOption[];
  /** Loading state for questionários */
  isLoadingQuestionarios?: boolean;
  /** Error loading questionários */
  questionariosError?: boolean;
}

/**
 * VariableHelper - Displays and manages template variables
 * @param root0
 * @param root0.questionarioId
 * @param root0.onQuestionarioChange
 * @param root0.onVariableSelect
 * @param root0.compact
 * @param root0.questionarios
 * @param root0.isLoadingQuestionarios
 * @param root0.questionariosError
 */
export function VariableHelper({
  questionarioId,
  onQuestionarioChange,
  onVariableSelect,
  compact = false,
  questionarios = [],
  isLoadingQuestionarios = false,
  questionariosError = false
}: VariableHelperProperties) {
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const clipboard = useClipboard({ timeout: 2000 });
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);

  // Load grupos dynamically
  const { grupos, isLoading: isLoadingGrupos, error: gruposError } = useQuestionarioGroups(questionarioId);

  // Filter variables by search query
  const filteredVariables = useMemo(() => {
    if (!searchQuery.trim()) return TEMPLATE_VARIABLES;

    const query = searchQuery.toLowerCase();
    return TEMPLATE_VARIABLES.filter(
      (v) =>
        v.label.toLowerCase().includes(query) ||
        v.description.toLowerCase().includes(query) ||
        v.name.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group filtered variables by category
  const variablesByCategory = useMemo(() => {
    const grouped = new Map<VariableCategory, TemplateVariable[]>();

    for (const variable of filteredVariables) {
      const existing = grouped.get(variable.category) || [];
      grouped.set(variable.category, [...existing, variable]);
    }

    return grouped;
  }, [filteredVariables]);

  // Filter grupos by search query
  const filteredGrupos = useMemo(() => {
    if (!searchQuery.trim()) return grupos;

    const query = searchQuery.toLowerCase();
    return grupos.filter(
      (g) =>
        g.nome.toLowerCase().includes(query) ||
        g.descricao?.toLowerCase().includes(query) ||
        `grupo[${g.ordem}]`.includes(query)
    );
  }, [grupos, searchQuery]);

  // Handler for search input change - avoid inline function in JSX
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value);
  }, []);

  // Handler for clearing search - avoid inline function in JSX
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  // Handle copy to clipboard
  const handleCopyVariable = (variableName: string, displayName: string) => {
    const formattedVariable = `{{${variableName}}}`;
    clipboard.copy(formattedVariable);

    // Mark this specific variable as copied
    setCopiedVariable(variableName);
    setTimeout(() => setCopiedVariable(null), 2000);

    notifications.show({
      title: "Variável copiada",
      message: `${displayName} copiado para área de transferência`,
      color: "green",
      icon: <IconCheck size={16} />
    });

    onVariableSelect?.(formattedVariable);
  };

  // Handle copy grupo variable (using ordem-based syntax)
  const handleCopyGrupoVariable = (grupo: GrupoForVariable, property: GroupVariableProperty) => {
    const formattedVariable = `{{grupo[${grupo.ordem}].${property.property}}}`;
    clipboard.copy(formattedVariable);

    // Mark this specific variable as copied (use unique key)
    const uniqueKey = `grupo[${grupo.ordem}].${property.property}`;
    setCopiedVariable(uniqueKey);
    setTimeout(() => setCopiedVariable(null), 2000);

    notifications.show({
      title: "Variável copiada",
      message: `${grupo.nome} (Ordem ${grupo.ordem}) - ${property.label}`,
      color: "green",
      icon: <IconCheck size={16} />
    });

    onVariableSelect?.(formattedVariable);
  };

  // Get category icon
  const getCategoryIcon = (category: VariableCategory) => {
    switch (category) {
      case VariableCategory.EMPRESA: {
        return <IconBuilding size={16} />;
      }
      case VariableCategory.QUESTIONARIO: {
        return <IconFileText size={16} />;
      }
      case VariableCategory.RESULTADOS: {
        return <IconChartBar size={16} />;
      }
      case VariableCategory.COMPONENTES: {
        return <IconComponents size={16} />;
      }
      case VariableCategory.GRUPOS: {
        return <IconDimensions size={16} />;
      }
      default: {
        return null;
      }
    }
  };

  // Get category label
  const getCategoryLabel = (category: VariableCategory) => {
    switch (category) {
      case VariableCategory.EMPRESA: {
        return "Empresa";
      }
      case VariableCategory.QUESTIONARIO: {
        return "Questionário / Projeto";
      }
      case VariableCategory.RESULTADOS: {
        return "Resultados";
      }
      case VariableCategory.COMPONENTES: {
        return "Componentes Visuais";
      }
      case VariableCategory.GRUPOS: {
        return "Grupos / Dimensões";
      }
      default: {
        return category;
      }
    }
  };

  return (
    <Stack gap="md">
      {/* Search input */}
      <TextInput
        placeholder="Buscar variáveis..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={handleSearchChange}
        rightSection={
          searchQuery && (
            <ActionIcon variant="subtle" onClick={handleClearSearch}>
              ×
            </ActionIcon>
          )
        }
      />

      {/* Results count */}
      {searchQuery && (
        <Text size="xs" c="dimmed">
          {filteredVariables.length + filteredGrupos.length * GROUP_VARIABLE_PROPERTIES.length} variáveis encontradas
        </Text>
      )}

      {/* Variables accordion */}
      <Accordion multiple>
        {/* Fixed variable categories */}
        {[...variablesByCategory.entries()].map(([category, variables]) => (
          <Accordion.Item key={category} value={category}>
            <Accordion.Control icon={getCategoryIcon(category)}>
              <Group justify="space-between" pr="md">
                <Text fw={500}>{getCategoryLabel(category)}</Text>
                <Badge size="sm" variant="light">
                  {variables.length}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {variables.map((variable) => (
                  <VariableCard
                    key={variable.name}
                    variable={variable}
                    compact={compact}
                    copiedVariable={copiedVariable}
                    onCopy={handleCopyVariable}
                  />
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}

        {/* Dynamic grupos section */}
        <Accordion.Item value="grupos">
          <Accordion.Control icon={getCategoryIcon(VariableCategory.GRUPOS)}>
            <Group justify="space-between" pr="md">
              <Text fw={500}>{getCategoryLabel(VariableCategory.GRUPOS)}</Text>
              <Badge size="sm" variant="light">
                {filteredGrupos.length} {filteredGrupos.length === 1 ? "grupo" : "grupos"}
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            {/* Questionário Selector - Inline com a seção de Grupos */}
            {onQuestionarioChange && (
              <Box mb="md">
                <Select
                  placeholder={
                    isLoadingQuestionarios
                      ? "Carregando questionários..."
                      : questionariosError
                        ? "Erro ao carregar"
                        : questionarios.length === 0
                          ? "Nenhum questionário disponível"
                          : "Selecionar questionário para preview"
                  }
                  data={questionarios.map((q) => ({ value: q.id, label: q.nome }))}
                  value={questionarioId ?? null}
                  onChange={onQuestionarioChange}
                  clearable
                  searchable
                  disabled={isLoadingQuestionarios || questionarios.length === 0}
                  leftSection={<IconBook size={16} />}
                  size="sm"
                  error={questionariosError ? "Erro ao carregar questionários" : undefined}
                  styles={{
                    input: { fontWeight: 400 }
                  }}
                />
              </Box>
            )}

            {/* Loading state */}
            {isLoadingGrupos && (
              <Group justify="center" p="md">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Carregando grupos...
                </Text>
              </Group>
            )}

            {/* Error state */}
            {gruposError && (
              <Alert icon={<IconAlertCircle size={16} />} title="Erro ao carregar grupos" color="red" variant="light">
                Não foi possível carregar os grupos do questionário.
              </Alert>
            )}

            {/* Empty state */}
            {!isLoadingGrupos && !gruposError && filteredGrupos.length === 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Nenhum grupo encontrado"
                color="yellow"
                variant="light"
              >
                {questionarioId
                  ? "Este questionário não possui grupos configurados."
                  : "Selecione um questionário acima para visualizar os grupos disponíveis."}
              </Alert>
            )}

            {/* Grupos list */}
            {!isLoadingGrupos && !gruposError && filteredGrupos.length > 0 && (
              <Accordion multiple>
                {filteredGrupos.map((grupo) => {
                  return (
                    <Accordion.Item key={grupo.id} value={grupo.id}>
                      <Accordion.Control>
                        <Group gap="xs" wrap="nowrap">
                          <Badge size="sm" variant="filled" color="blue" radius="sm">
                            {grupo.ordem}
                          </Badge>
                          <Text size="sm" fw={500} lineClamp={1}>
                            {grupo.nome}
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs">
                          {GROUP_VARIABLE_PROPERTIES.map((property) => (
                            <GrupoVariableCard
                              key={`grupo[${grupo.ordem}].${property.property}`}
                              grupo={grupo}
                              property={property}
                              compact={compact}
                              copiedVariable={copiedVariable}
                              onCopy={handleCopyGrupoVariable}
                            />
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
